'use client'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import React, { Component, useEffect, useState, type ErrorInfo, type ReactNode } from 'react'

import { IDENTITY_QUERY_POLICY, MILLS_QUERY_KEY } from '../../lib/mills/public-client'

type ModuleState = 'ready' | 'loading' | 'empty' | 'stale' | 'unavailable'

const stateMessage: Record<Exclude<ModuleState, 'ready'>, string> = {
  loading: 'Loading live football data…',
  empty: 'There is no live football data to show yet.',
  stale: 'Showing the latest available football data.',
  unavailable: 'Live football data is temporarily unavailable.',
}

type ErrorBoundaryProps = { children: ReactNode; fallback: ReactNode }
type ErrorBoundaryState = { failed: boolean }

class MillsErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { failed: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { failed: true }
  }

  componentDidCatch(_error: Error, _info: ErrorInfo) {
    // The host application can add bounded error reporting without exposing payloads.
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children
  }
}

type MillsModuleBoundaryProps = {
  children: ReactNode
  state?: ModuleState
}

function ModuleStatus({ state }: { state: Exclude<ModuleState, 'ready'> }) {
  return <p role="status">{stateMessage[state]}</p>
}

export function MillsModuleBoundary({ children, state = 'ready' }: MillsModuleBoundaryProps) {
  const [ownedQueryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: IDENTITY_QUERY_POLICY.staleTime,
            gcTime: IDENTITY_QUERY_POLICY.gcTime,
            refetchInterval: IDENTITY_QUERY_POLICY.refreshInterval,
            refetchIntervalInBackground: true,
            retry: 1,
          },
        },
      })
  )
  const queryClient = ownedQueryClient

  useEffect(() => {
    const refreshTimer = window.setInterval(() => {
      queryClient.removeQueries({ queryKey: MILLS_QUERY_KEY, type: 'inactive' })
      void queryClient.resetQueries({ queryKey: MILLS_QUERY_KEY, type: 'active' })
    }, IDENTITY_QUERY_POLICY.refreshInterval)

    return () => {
      window.clearInterval(refreshTimer)
      void queryClient.cancelQueries({ queryKey: MILLS_QUERY_KEY })
      queryClient.removeQueries({ queryKey: MILLS_QUERY_KEY })
    }
  }, [queryClient])

  const fallback = <ModuleStatus state="unavailable" />
  return (
    <QueryClientProvider client={queryClient}>
      <MillsErrorBoundary fallback={fallback}>
        {state === 'ready' ? children : null}
        {state === 'stale' ? (
          <>
            <ModuleStatus state="stale" />
            {children}
          </>
        ) : null}
        {state !== 'ready' && state !== 'stale' ? <ModuleStatus state={state} /> : null}
      </MillsErrorBoundary>
    </QueryClientProvider>
  )
}
